import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Form, Row, Col, Alert } from "react-bootstrap";
import Select from "react-select";
import "./hr-timing.css";
import EditHrTimingLayout from "../../components/EditHrTimingLayout.jsx";

export default function EditHrTiming() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [timing, setTiming] = useState({
    shift: null,
    checkIn: "",
    checkOut: "",
    breakIn: "",
    breakOut: "",
  });
  const [loading, setLoading] = useState(true);
  const [totalHours, setTotalHours] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("success");

  const shiftOptions = useMemo(
    () => [
      { value: "AM", label: "Morning" },
      { value: "PM", label: "Evening" },
    ],
    []
  );

  useEffect(() => {
    const fetchTiming = async () => {
      try {
        const response = await fetch(
          `https://backend.ems.uk.hiradentallab.com/api/timings/${userId}`
        );
        if (response.ok) {
          const data = await response.json();
          setTiming({
            shift:
              shiftOptions.find((option) => option.value === data.shift) ||
              null,
            checkIn: data.shiftStart,
            checkOut: data.shiftEnd,
            breakIn: data.breakStart,
            breakOut: data.breakEnd,
          });
          calculateTotalHours(
            data.shiftStart,
            data.shiftEnd,
            data.breakStart,
            data.breakEnd
          );
        } else {
          console.log("No timing data found for this user");
        }
      } catch (error) {
        console.error("Error fetching timing data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTiming();
  }, [userId, shiftOptions]);

  const calculateTotalHours = (checkIn, checkOut, breakIn, breakOut) => {
    const start = new Date(`1970-01-01T${checkIn}:00`);
    const end = new Date(`1970-01-01T${checkOut}:00`);
    const breakStart = new Date(`1970-01-01T${breakIn}:00`);
    const breakEnd = new Date(`1970-01-01T${breakOut}:00`);

    const totalWorkHours =
      (end - start - (breakEnd - breakStart)) / (1000 * 60 * 60);
    setTotalHours(totalWorkHours);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTiming((prev) => {
      const newTiming = { ...prev, [name]: value };

      // Automatically set shift based on check-in time
      if (name === "checkIn") {
        const checkInHour = parseInt(value.split(":")[0], 10);
        newTiming.shift = checkInHour < 12 ? shiftOptions[0] : shiftOptions[1]; // Morning for AM, Evening for PM
      }

      calculateTotalHours(
        newTiming.checkIn,
        newTiming.checkOut,
        newTiming.breakIn,
        newTiming.breakOut
      );
      return newTiming;
    });
  };

  const handleShiftChange = (selectedOption) => {
    setTiming((prev) => ({ ...prev, shift: selectedOption }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `https://backend.ems.uk.hiradentallab.com/api/timings/${userId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            shiftStart: timing.checkIn,
            shiftEnd: timing.checkOut,
            breakStart: timing.breakIn,
            breakEnd: timing.breakOut,
            totalHours: totalHours,
            shift: timing.shift?.value,
          }),
        }
      );
      if (response.ok) {
        const updatedTiming = await response.json();
        setTiming({
          ...updatedTiming,
          shift:
            shiftOptions.find(
              (option) => option.value === updatedTiming.shift
            ) || null,
        });
        setAlertMessage("Timing data updated successfully");
        setShowAlert(true);
        setAlertVariant("success");

        setTimeout(() => {
          navigate("/hr-schedule");
        }, 3000);
      } else {
        const errorData = await response.json();
        setAlertMessage(
          "Failed to update timing data: " +
            (errorData.message || "An error occurred")
        );
        setShowAlert(true);
        setAlertVariant("danger");
      }
    } catch (error) {
      console.error("Error updating timing data:", error);
      setAlertMessage("An error occurred while updating timing data.");
      setShowAlert(true);
      setAlertVariant("danger");
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <EditHrTimingLayout>
      <div className="emp-timing-container">
        {showAlert && (
          <Alert
            variant={alertVariant}
            onClose={() => setShowAlert(false)}
            dismissible
          >
            {alertMessage}
          </Alert>
        )}
        <Form className="emp-timing-form" onSubmit={handleUpdate}>
          <Row>
            <Col md={6}>
              <Form.Group controlId="checkIn">
                <Form.Label>
                  Check-IN <span className="required-asterisk">*</span>
                </Form.Label>
                <Form.Control
                  type="time"
                  name="checkIn"
                  required
                  value={timing.checkIn}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="checkOut">
                <Form.Label>
                  Check-Out <span className="required-asterisk">*</span>
                </Form.Label>
                <Form.Control
                  type="time"
                  name="checkOut"
                  required
                  value={timing.checkOut}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group controlId="breakIn">
                <Form.Label>
                  Break-IN <span className="required-asterisk">*</span>
                </Form.Label>
                <Form.Control
                  type="time"
                  name="breakIn"
                  required
                  value={timing.breakIn}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="breakOut">
                <Form.Label>
                  Break-Out <span className="required-asterisk">*</span>
                </Form.Label>
                <Form.Control
                  type="time"
                  name="breakOut"
                  required
                  value={timing.breakOut}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Button className="mt-3" variant="primary" type="submit">
            Update
          </Button>
        </Form>
      </div>
      <div className="emp-shift-container">
        <Form.Group controlId="shift-select">
          <Form.Label className="shift-label">Selected Shift:</Form.Label>
          <Select
            className="shift-select"
            options={shiftOptions}
            onChange={handleShiftChange}
            value={timing.shift}
            isDisabled // Disable manual selection since shift is auto-determined
          />
        </Form.Group>
      </div>
    </EditHrTimingLayout>
  );
}
